import {
  ListItemsResponse,
  MyKnownError,
  RequestCandidateItemResponse,
  RequestItem,
  RequestItemResponse,
  RequestsService,
  RequestLoggerItemResponse,
  RequestsState,
} from '@innowise-group/core';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RequestCandidateDecisionEnum, RequestPriorityBoost } from '@constants';
import { delay } from '@innowise-group/utilities';

import {
  getRequestPriorityChange,
  localizeRequest,
  mapRequestItemToPayload,
  mapRequestResponseToRequestItem,
} from './requests.mappers';

export const createRequestThunk = createAsyncThunk<
  RequestItemResponse,
  { data: RequestItem; successCallback?: (requestId: number) => void },
  { rejectValue: MyKnownError }
>('requests/create', async ({ data, successCallback }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.createRequest(mapRequestItemToPayload(data));
    successCallback?.(response.data.id);
    return response.data;
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

export const getRequestByIdThunk = createAsyncThunk<RequestItemResponse, { id: number }, { rejectValue: MyKnownError }>(
  'requests/getItem',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await RequestsService.getVacancyRequestById(id);
      return { ...response.data, linkedCandidatesIsLoading: false, linkedCandidatesIds: [], linkedCandidatesItems: {} };
    } catch (err) {
      return rejectWithValue({
        errorMessage: err.message,
      } as MyKnownError);
    }
  },
);

// The status and the text of the backend tell the deletion modal why the request was not deleted (US-10.5 AC-6)
export const deleteRequestThunk = createAsyncThunk<
  void,
  { id: number; successCallback?: () => void; ignoreGlobalCatch?: boolean },
  { rejectValue: MyKnownError & { status?: number; message?: string } }
>('requests/delete', async ({ id, successCallback, ignoreGlobalCatch }, { rejectWithValue }) => {
  try {
    await RequestsService.deleteRequestById(id, ignoreGlobalCatch ? { ignoreGlobalCatch } : undefined);
    successCallback?.();
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
      status: err.response?.status,
      message: err.response?.data?.message,
    });
  }
});

export const updateRequestExecutorsThunk = createAsyncThunk<
  RequestItemResponse,
  { id: number; executorIds: number[] },
  { rejectValue: MyKnownError }
>('requests/updateExecutors', async ({ id, executorIds }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.updateExecutors(id, executorIds);
    return response.data;
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

export const updateRequestPriorityBoostThunk = createAsyncThunk<
  RequestItemResponse,
  { id: number; value: RequestPriorityBoost },
  { rejectValue: MyKnownError }
>('requests/updatePriorityBoost', async ({ id, value }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.updatePriorityBoost(id, value);
    return response.data;
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

// A priority tag is the base priority plus the boost. A new base is saved with the whole request, as the form does
// (PUT replaces every field of the form but not the boost), then the boost. When the boost fails after the PUT, the
// rejection carries the PUT answer, so the row shows the base that is saved
export const updateRequestPriorityThunk = createAsyncThunk<
  RequestItemResponse,
  { id: number; priorityTag: string },
  // The slice only: the whole RootState would make the store type depend on itself
  { state: { requests: RequestsState }; rejectValue: MyKnownError & { request?: RequestItemResponse } }
>('requests/updatePriority', async ({ id, priorityTag }, { getState, rejectWithValue }) => {
  const request = getState().requests.requests.items[id];
  const { priority, priorityBoost } = getRequestPriorityChange(request, priorityTag);
  let savedRequest: RequestItemResponse | null = null;

  try {
    if (priority !== request.priority) {
      const data = { ...mapRequestResponseToRequestItem(localizeRequest(request)), priority };
      savedRequest = (await RequestsService.updateRequest(mapRequestItemToPayload(data), id)).data;
    }
    if (priorityBoost !== (request.priorityBoost || 0)) {
      savedRequest = (await RequestsService.updatePriorityBoost(id, priorityBoost)).data;
    }
    return savedRequest || request;
  } catch (err) {
    return rejectWithValue({ errorMessage: err.message, ...(savedRequest && { request: savedRequest }) });
  }
});

// Closing changes the status on the backend, so the card re-reads the whole request afterwards
export const closeRequestThunk = createAsyncThunk<RequestItemResponse, { id: number }, { rejectValue: MyKnownError }>(
  'requests/close',
  async ({ id }, { rejectWithValue }) => {
    try {
      await RequestsService.closeRequest(id);
      const response = await RequestsService.getVacancyRequestById(id);
      return response.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err.message,
      } as MyKnownError);
    }
  },
);

export const updateRequestThunk = createAsyncThunk<
  RequestItemResponse,
  {
    data: RequestItem;
    id: number;
    successCallback?: (requestId: number) => void;
    // the caller shows its own message instead of the toast of the backend error
    ignoreGlobalCatch?: boolean;
  },
  { rejectValue: MyKnownError }
>('requests/update', async ({ data, id, successCallback, ignoreGlobalCatch }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.updateRequest(
      mapRequestItemToPayload(data),
      id,
      ignoreGlobalCatch ? { ignoreGlobalCatch } : undefined,
    );

    if (typeof successCallback === 'function') {
      await delay(500);
      successCallback(response.data.id);
    }

    return response.data;
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

export const getAllRequestsThunk = createAsyncThunk<
  ListItemsResponse<RequestItemResponse>,
  Parameters<typeof RequestsService.getAllRequests>[0],
  { rejectValue: MyKnownError }
>('requests/getAll', async (data, { rejectWithValue }) => {
  try {
    const response = await RequestsService.getAllRequests(data);
    return {
      ...response.data,
      content: response.data.content.map((item) => {
        return { ...item, linkedCandidatesIsLoading: false, linkedCandidatesIds: [], linkedCandidatesItems: {} };
      }),
    };
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

export const exportRequestsThunk = createAsyncThunk<
  void,
  Parameters<typeof RequestsService.requestsExport>[0],
  { rejectValue: MyKnownError }
>('requests/export', async (body, { rejectWithValue }) => {
  try {
    const response = await RequestsService.requestsExport(body);
    const data = URL.createObjectURL(response.data);
    const fileUrl = document.createElement('a');
    fileUrl.href = data;
    fileUrl.download = 'export-results.xlsx';
    document.body.appendChild(fileUrl);
    fileUrl.click();
    URL.revokeObjectURL(fileUrl.href);
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

export const getRequestCandidatesThunk = createAsyncThunk<
  RequestCandidateItemResponse[],
  { id: number },
  { rejectValue: MyKnownError }
>('requests/getCandidates', async ({ id }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.getRequestCandidates(id);
    return response.data;
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

// The status tells a candidate detached in the meantime (404) from the other failures
export const updateRequestCandidateDecisionThunk = createAsyncThunk<
  RequestCandidateItemResponse,
  { id: number; candidateId: number; value: RequestCandidateDecisionEnum },
  { rejectValue: MyKnownError & { status?: number } }
>('requests/updateCandidateDecision', async ({ id, candidateId, value }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.updateCandidateDecision(id, candidateId, value);
    return response.data;
  } catch (err) {
    return rejectWithValue({ errorMessage: err.message, status: err.response?.status });
  }
});

export const detachRequestCandidateThunk = createAsyncThunk<
  void,
  { id: number; candidateId: number },
  { rejectValue: MyKnownError }
>('requests/detachCandidate', async ({ id, candidateId }, { rejectWithValue }) => {
  try {
    await RequestsService.detachCandidate(id, candidateId);
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});

export const getRequestLoggerDataThunk = createAsyncThunk<
  ListItemsResponse<RequestLoggerItemResponse>,
  { requestId: number; params: { page: number; size: number } },
  { rejectValue: MyKnownError }
>('requests/getRequestLoggerData', async ({ params, requestId }, { rejectWithValue }) => {
  try {
    const response = await RequestsService.getHistoryByRequest(requestId, params);
    return response.data;
  } catch (err) {
    return rejectWithValue({
      errorMessage: err.message,
    } as MyKnownError);
  }
});
